<template>
	<player id="VRViewer" class="ui ui--vr">
		<div ref="rendererContainer"></div>

		<ElementButton name="close" @click="closeAndRemoveScreen" class="ui__vr__close" i18n content="close" />
		<ElementMedia element-type="image" />
	</player>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import screenfull from 'screenfull';
import UiBase from './base.vue';
import { uiComponents } from '../../tools/viewState';
import { isIOS } from '../../tools/helpers';
import errorHandler from '../../tools/errorHandler';
import { logger } from '../../tools';
import { getterTypes } from '../../types';

const supportsFullscreen = screenfull.enabled && !isIOS();

export default {
	extends: UiBase,
	data() {
		return {
			name: uiComponents.uiVr,
			ready: false,
		};
	},
	computed: {
		...mapState(['playerVrViewer']),
		...mapGetters([getterTypes.historyCur]),
	},
	methods: {
		...mapActions(['closeModal']),
		closeAndRemoveScreen() {
			if (supportsFullscreen) screenfull.exit();
			this.close();
		},
		async initScene() {
			window.vr = this;
			const THREE = await import(/* webpackChunkName: "threejs_exports" */ '../../tools/threeExports');

			// return early if the viewer was closed while awaiting import
			if (!this.isOpen) return;

			this.renderer = new THREE.WebGLRenderer();
			this.renderer.setPixelRatio(window.devicePixelRatio);
			this.renderer.setSize(window.innerWidth, window.innerHeight);
			this.$refs.rendererContainer.appendChild(this.renderer.domElement);

			this.scene = new THREE.Scene();
			this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 1, 1100);

			const geometry = new THREE.SphereBufferGeometry(500, 60, 40);
			const texture = new THREE.TextureLoader().load(this.playerVrViewer.url);
			const material = new THREE.MeshBasicMaterial({ map: texture });

			// invert the geometry on the x-axis so that all of the faces point inward
			geometry.scale(-1, 1, 1);
			const mesh = new THREE.Mesh(geometry, material);
			this.scene.add(mesh);

			// use orbit controls until deviceorientation event is fired
			this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
			this.controls.target.x = 1;
			this.controls.enableZoom = false;
			this.controls.enablePan = false;
			this.controls.enableDamping = false;
			this.controls.rotateSpeed = -0.365;

			// replae orbit with device orientation on event
			window.addEventListener('deviceorientation', this.onDeviceOrientation);
			window.addEventListener('resize', this.onWindowResize);
			if (supportsFullscreen) {
				window.addEventListener(screenfull.raw.fullscreenchange, this.onFullscreenChange);
			}

			this.ready = true;
		},
		disposeScene() {
			if (!this.ready) return;

			this.renderer.dispose();
			this.controls.dispose();
			this.renderer.domElement.remove();

			this.scene = null;
			this.camera = null;
			this.renderer = null;
			this.controls = null;

			window.cancelAnimationFrame(this.rafToken);
			window.removeEventListener('deviceorientation', this.onDeviceOrientation);
			window.removeEventListener('resize', this.onWindowResize);
			if (supportsFullscreen) {
				window.removeEventListener(screenfull.raw.fullscreenchange, this.onFullscreenChange);
			}
		},

		animate() {
			this.rafToken = requestAnimationFrame(() => this.animate());
			this.controls.update();
			this.renderer.render(this.scene, this.camera);
		},
	},
	watch: {
		[getterTypes.historyCur](cur, old) {
			// close this vr viewer on history change
			this.close();

			const isOpenWithoutFullscreen = this.isOpen && (!supportsFullscreen || !screenfull.isFullscreen);

			// if fullscreen isn't supported, stay on the previous article to avoid
			// confusion when trying to use the back button to exit the viewer
			if (isOpenWithoutFullscreen) {
				const historyDirection = cur.index - old.index;

				if (historyDirection === -1) {
					this.$router.forward();
				}
			}
		},
	},
	beforeCreate() {
		this.onDeviceOrientation = async ({ alpha, beta, gamma }) => {
			if (!(alpha && beta && gamma) || !this.ready) return;

			window.removeEventListener('deviceorientation', this.onDeviceOrientation);

			const THREE = await getThree();
			this.controls.dispose();
			this.controls = new THREE.DeviceOrientationControls(this.camera);
		};

		this.onWindowResize = () => {
			this.camera.aspect = window.innerWidth / window.innerHeight;
			this.camera.updateProjectionMatrix();
			this.renderer.setSize(window.innerWidth, window.innerHeight);
		};

		this.onFullscreenChange = () => {
			if (!screenfull.isFullscreen) {
				this.close();
			}
		};
	},
	async mounted() {
		logger.log('post360: create viewer', { ...this.playerVrViewer });
		if (supportsFullscreen) {
			screenfull.request(this.$el);
		}
		try {
			await this.initScene();
			this.animate();
		} catch (e) {
			console.error(e);
			errorHandler(new Error(errorTypes.vr360OpenFailed));
			this.closeAndRemoveScreen();
		}
	},
	beforeDestroy() {
		this.disposeScene();
	},
};
</script>
